import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Lock, Check, ChevronRight, Sparkles } from 'lucide-react';
import { usePremium } from '@/hooks/usePremium';
import type { LearningPath as LPType, Unit, DifficultyLevel } from '@/data/lessons';

interface SkillTreeProps {
  paths: LPType[];
  completedLessons: string[];
  totalXp: number;
  onSelectLesson: (lessonId: string) => void;
  userSkillLevel?: DifficultyLevel | null;
  assignedLessonIds?: string[];
}

function PathConnector({ fromSide, toSide }: { fromSide: 'left' | 'center' | 'right'; toSide: 'left' | 'center' | 'right' }) {
  // SVG curve connecting two nodes in the winding path
  const xMap = { left: 25, center: 50, right: 75 };
  const x1 = xMap[fromSide];
  const x2 = xMap[toSide];

  return (
    <div className="w-full h-10 relative">
      <svg width="100%" height="100%" viewBox="0 0 100 40" preserveAspectRatio="none" className="absolute inset-0">
        <path
          d={`M ${x1} 0 C ${x1} 20, ${x2} 20, ${x2} 40`}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="2"
          strokeDasharray="4 3"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

function JourneyNode({
  unit,
  index,
  totalXp,
  completedLessons,
  onSelectLesson,
  isNextAvailable,
  isRecommended,
  side,
  assignedLessonIds = [],
}: {
  unit: Unit;
  index: number;
  totalXp: number;
  completedLessons: string[];
  onSelectLesson: (id: string) => void;
  isNextAvailable: boolean;
  isRecommended: boolean;
  side: 'left' | 'center' | 'right';
  assignedLessonIds?: string[];
}) {
  const isLocked = totalXp < unit.requiredXp;
  const completedCount = unit.lessons.filter(l => completedLessons.includes(l.id)).length;
  const isComplete = completedCount === unit.lessons.length && unit.lessons.length > 0;
  const inProgress = !isLocked && !isComplete && completedCount > 0;
  const nextLesson = unit.lessons.find(l => !completedLessons.includes(l.id));
  const isCurrent = isNextAvailable && !isLocked && !isComplete;
  const hasAssigned = unit.lessons.some(l => assignedLessonIds.includes(l.id));

  const handleClick = () => {
    if (isLocked) return;
    if (nextLesson) {
      onSelectLesson(nextLesson.id);
    } else if (unit.lessons.length > 0) {
      onSelectLesson(unit.lessons[0].id);
    }
  };

  const alignClass = side === 'left' ? 'mr-auto' : side === 'right' ? 'ml-auto' : 'mx-auto';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.06, duration: 0.35, type: 'spring', stiffness: 200 }}
      className={`w-[75%] max-w-[320px] ${alignClass}`}
    >
      <button
        onClick={handleClick}
        disabled={isLocked}
        className={`w-full flex items-center gap-3.5 rounded-2xl px-4 py-3.5 transition-all text-left relative ${
          isCurrent
            ? 'bg-primary/10 border-2 border-primary shadow-md ring-2 ring-primary/20'
            : isComplete
              ? 'bg-card border-2 border-success/30'
              : isLocked
                ? 'opacity-35 cursor-not-allowed bg-muted/30 border-2 border-transparent'
                : 'bg-card border-2 border-border/50 hover:border-primary/30 hover:shadow-sm'
        }`}
      >
        {/* Pulsing indicator for current */}
        {isCurrent && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-primary" />
          </span>
        )}

        {/* Status icon */}
        <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
          isComplete
            ? 'bg-success/15'
            : isCurrent
              ? 'bg-primary/15'
              : inProgress
                ? 'bg-accent/15'
                : 'bg-muted'
        }`}>
          {isComplete ? (
            <Check className="w-5 h-5 text-success" />
          ) : isLocked ? (
            <Lock className="w-4 h-4 text-muted-foreground" />
          ) : (
            <span className="text-lg">{unit.icon}</span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`font-display font-bold text-sm truncate ${
              isLocked ? 'text-muted-foreground' : 'text-foreground'
            }`}>
              {unit.title}
            </p>
            {isRecommended && !isComplete && !isLocked && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-accent/15 text-accent text-[9px] font-display font-bold flex-shrink-0">
                <Sparkles className="w-2.5 h-2.5" />
                For you
              </span>
            )}
            {hasAssigned && !isComplete && !isLocked && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[9px] font-display font-bold flex-shrink-0">
                📋 Assigned
              </span>
            )}
          </div>
          {isCurrent && nextLesson && (
            <p className="text-xs text-primary font-display mt-0.5 truncate">
              {inProgress ? 'Resume' : 'Start'} · {nextLesson.title}
            </p>
          )}
          {isComplete && (
            <p className="text-xs text-success/70 mt-0.5">Complete</p>
          )}
          {inProgress && !isCurrent && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {completedCount}/{unit.lessons.length} done
            </p>
          )}
        </div>

        {isCurrent && (
          <ChevronRight className="w-5 h-5 text-primary flex-shrink-0" />
        )}
      </button>
    </motion.div>
  );
}

// Winding pattern: center → right → center → left → center → right ...
const SIDE_PATTERN: Array<'left' | 'center' | 'right'> = [
  'center', 'right', 'center', 'left',
];

function getSide(index: number): 'left' | 'center' | 'right' {
  return SIDE_PATTERN[index % SIDE_PATTERN.length];
}

export function SkillTree({ paths, completedLessons, totalXp, onSelectLesson, userSkillLevel, assignedLessonIds = [] }: SkillTreeProps) {
  const { isPremium } = usePremium();
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  const allUnits: { unit: Unit; pathId: string; pathTitle: string; difficulty: string }[] = [];
  for (const path of paths) {
    for (const unit of path.units) {
      allUnits.push({ unit, pathId: path.id, pathTitle: path.title, difficulty: path.difficulty });
    }
  }

  const recommendedStartIndex = userSkillLevel
    ? allUnits.findIndex(item => {
        const isLocked = totalXp < item.unit.requiredXp;
        const isComplete = item.unit.lessons.every(l => completedLessons.includes(l.id)) && item.unit.lessons.length > 0;
        return item.difficulty === userSkillLevel && !isLocked && !isComplete;
      })
    : -1;

  const nextAvailableIndex = allUnits.findIndex(item => {
    const isLocked = totalXp < item.unit.requiredXp;
    const isComplete = item.unit.lessons.every(l => completedLessons.includes(l.id)) && item.unit.lessons.length > 0;
    return !isLocked && !isComplete;
  });

  useEffect(() => {
    if (recommendedStartIndex > 0 && scrollTargetRef.current) {
      setTimeout(() => {
        scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [recommendedStartIndex]);

  return (
    <div className="relative py-2">
      {allUnits.map((item, i) => {
        const isRecommended = userSkillLevel ? item.difficulty === userSkillLevel : false;
        const isFirstRecommended = i === recommendedStartIndex;
        const side = getSide(i);
        const prevSide = i > 0 ? getSide(i - 1) : 'center';
        const showPathDivider = i > 0 && allUnits[i - 1].pathId !== item.pathId;
        const showDifficultyGate = i > 0 && allUnits[i - 1].difficulty !== item.difficulty;
        const showMilestone = showPathDivider || showDifficultyGate;

        // Milestone emoji based on difficulty
        const milestoneEmoji = (() => {
          if (showDifficultyGate) {
            if (item.difficulty === 'intermediate') return '⭐';
            if (item.difficulty === 'advanced') return '🔥';
          }
          return '📍';
        })();

        const milestoneLabel = showDifficultyGate
          ? (item.difficulty === 'intermediate' ? 'Varsity' : item.difficulty === 'advanced' ? 'Pro' : item.pathTitle)
          : item.pathTitle;

        return (
          <div key={item.unit.id} ref={isFirstRecommended ? scrollTargetRef : undefined}>
            {/* Milestone divider — subtle label between sections */}
            {showMilestone && (
              <div className="flex items-center gap-2 py-3 px-6">
                <div className="flex-1 h-px bg-border/60" />
                <span className="text-[10px] text-muted-foreground/70 font-display font-bold uppercase tracking-[0.15em] flex items-center gap-1">
                  <span className="text-xs">{milestoneEmoji}</span>
                  {milestoneLabel}
                </span>
                <div className="flex-1 h-px bg-border/60" />
              </div>
            )}

            {/* Connector line */}
            {i > 0 && !showMilestone && (
              <PathConnector fromSide={prevSide} toSide={side} />
            )}

            <JourneyNode
              unit={item.unit}
              index={i}
              totalXp={totalXp}
              completedLessons={completedLessons}
              onSelectLesson={onSelectLesson}
              isNextAvailable={i === nextAvailableIndex}
              isRecommended={isRecommended}
              side={side}
              assignedLessonIds={assignedLessonIds}
            />
          </div>
        );
      })}
    </div>
  );
}
